import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Globe2,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  MessageSquareWarning,
  ShieldCheck,
  Users,
} from "lucide-react";
import PortalSidebar from "../../admin-shared/PortalSidebar";
import { filterPortalMenuItems } from "../../admin-shared/filterPortalMenuItems";
import { permissionService } from "../services/permissionService";
import { useUser } from "../context/UserContext";
import { loginPathForRole } from "../utils/loginPaths";
import { api, clearPortalStorage, PORTAL_ID } from "../utils/api";
import {
  ORG_LOGO_UPDATED_EVENT,
  ENTERPRISE_DEFAULT_ORG_LOGO,
  resolveMediaUrl,
  resolvePartnerLogoSrc,
  resolveStoredOrgLogo,
} from "../utils/mediaUrl";
import { fetchOrganizationLogo } from "../utils/orgLogoLoader";

const Sidebar = ({ expanded, setExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUser() || {};
  const isFranchise = permissionService.isFranchise();
  const pickLogo = (orgLogo) =>
    resolveMediaUrl(orgLogo) ||
    resolveStoredOrgLogo() ||
    resolvePartnerLogoSrc(orgLogo) ||
    ENTERPRISE_DEFAULT_ORG_LOGO;

  const [logoSrc, setLogoSrc] = useState(() => pickLogo(user?.org_logo));
  const [viewport, setViewport] = useState("desktop");

  useEffect(() => {
    setLogoSrc(pickLogo(user?.org_logo));
  }, [user?.org_logo, isFranchise]);

  useEffect(() => {
    const onLogoUpdated = (event) => {
      setLogoSrc(pickLogo(event?.detail?.url));
    };
    window.addEventListener(ORG_LOGO_UPDATED_EVENT, onLogoUpdated);
    return () => window.removeEventListener(ORG_LOGO_UPDATED_EVENT, onLogoUpdated);
  }, [isFranchise]);

  useEffect(() => {
    if (!localStorage.getItem("naturals_executive_userId")) return undefined;

    let cancelled = false;
    (async () => {
      const logo = await fetchOrganizationLogo({ isFranchise });
      if (cancelled || !logo) return;
      const display = resolveMediaUrl(logo) || logo;
      setLogoSrc(pickLogo(display));
      if (typeof setUser === "function") {
        setUser((prev) => (prev ? { ...prev, org_logo: display } : prev));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setUser, isFranchise]);

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      if (width < 768) setViewport("mobile");
      else if (width < 1367) setViewport("tablet");
      else setViewport("desktop");
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const handleLogout = () => {
    const userId = localStorage.getItem(`naturals_${PORTAL_ID}_userId`);
    api(`/${PORTAL_ID}/logout`, {
      method: "POST",
      body: JSON.stringify({ userId, audience: PORTAL_ID }),
    }).catch(() => {});
    clearPortalStorage();
    navigate(loginPathForRole());
  };

  const onMainOverviewHub =
    location.pathname.replace(/\/$/, "") === "/naturals/executive/overview";

  const menuItems = useMemo(
    () =>
      filterPortalMenuItems(
        [
          {
            key: "overview",
            sectionId: "overview",
            IconComponent: LayoutGrid,
            label: onMainOverviewHub ? "Overview" : "Back to main",
            to: "/naturals/executive/overview",
            superAdminOnly: true,
          },
          { key: "super-admin-dashboard", sectionId: "overview", IconComponent: LayoutDashboard, label: "Dashboard", to: "/naturals/executive/super-admin-dashboard", superAdminOnly: true },
          { key: "check-in-analytics", sectionId: "overview", IconComponent: BarChart3, label: "Check-in analytics", to: "/naturals/executive/check-in-analytics", superAdminOnly: true },
          { key: "customers", sectionId: "overview", IconComponent: Users, label: "Customers", to: "/naturals/executive/customers", superAdminOnly: true },
          { key: "hq-admins", sectionId: "access", IconComponent: ShieldCheck, label: "Admin access", to: "/naturals/executive/hq-admins", superAdminOnly: true },
          { key: "naturals-kpis", sectionId: "network", IconComponent: LineChart, label: "Salon KPIs", to: "/naturals/executive/naturals-kpis", superAdminOnly: true },
          { key: "complaints", sectionId: "network", IconComponent: MessageSquareWarning, label: "Complaints", to: "/naturals/executive/complaints", superAdminOnly: true },
          { key: "regions", sectionId: "organization", IconComponent: Globe2, label: "Regions", to: "/naturals/executive/regions", superAdminOnly: true },
          { key: "licenses", sectionId: "organization", IconComponent: KeyRound, label: "Licenses", to: "/naturals/executive/licenses", superAdminOnly: true },
          { key: "company-profile", sectionId: "organization", IconComponent: Building2, label: "Company profile", to: "/naturals/executive/company-profile", superAdminOnly: true, adminPermission: "company_profile" },
        ],
        permissionService,
      ),
    [onMainOverviewHub],
  );

  const isCompactViewport = viewport === "tablet" || viewport === "mobile";

  return (
    <PortalSidebar
      menuItems={menuItems}
      logoSrc={logoSrc}
      logoFallback={ENTERPRISE_DEFAULT_ORG_LOGO}
      isFranchise={isFranchise}
      expanded={expanded}
      setExpanded={setExpanded}
      onLogout={handleLogout}
      isCompactViewport={isCompactViewport}
      locationPathname={location.pathname}
    />
  );
};

export default Sidebar;
